'use client'
import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'next/navigation'
import { getCookies } from 'cookies-next'
import { countryCodes } from '@constants/countryCodes'
import { preferredContact } from '@constants/preferredContact'

// ── Resolve multilingual value ────────────────────────────────────────────────
function r(val, lang) {
  if (!val) return ''
  if (typeof val === 'string') return val
  return val[lang] ?? val.en ?? Object.values(val)[0] ?? ''
}

// ── Icons ─────────────────────────────────────────────────────────────────────
const Icons = {
  user:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>,
  email: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>,
  phone: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.56 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.29 6.29l1.27-.88a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  line:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5C21 6.8 16.97 3 12 3S3 6.8 3 11.5c0 4.2 3.37 7.73 8 8.63v-2.94c-3.07-.74-5-2.7-5-5.69 0-3.03 2.69-5.5 6-5.5s6 2.47 6 5.5c0 .65-.13 1.28-.36 1.87L16 14.5v2h-4v-2h2.08A4.5 4.5 0 0 1 7.5 11.5"/></svg>,
  wechat:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.5 9a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z" fill="currentColor"/><path d="M14.5 9a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z" fill="currentColor"/><path d="M8.5 15a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z" fill="currentColor"/><path d="M12.5 15a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z" fill="currentColor"/><path d="M2 12c0 4.4 4.03 8 9 8 1.3 0 2.53-.26 3.63-.72L20 21l-1.5-4.05C19.46 15.56 21 13.87 21 12c0-4.42-4.03-8-9-8s-9 3.58-9 8z"/></svg>,
  money: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2"/><path d="M6 12h.01M18 12h.01"/></svg>,
  area:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 3v18"/></svg>,
  none:  null,
}

// ── Country combobox ──────────────────────────────────────────────────────────
function CountryCombobox({ value, onChange, inputStyle, radius }) {
  const [query,   setQuery]   = useState('')
  const [open,    setOpen]    = useState(false)
  const [hovered, setHovered] = useState(null)
  const ref = useRef(null)

  const list = query === ''
    ? countryCodes
    : countryCodes.filter(c =>
        c.country.toLowerCase().includes(query.toLowerCase()) ||
        c.code.toLowerCase().includes(query.toLowerCase())
      )

  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQuery('') } }
    document.addEventListener('mousedown', fn)
    return () => document.removeEventListener('mousedown', fn)
  }, [])

  const pick = (code) => { onChange(code); setQuery(''); setOpen(false) }

  return (
    <div ref={ref} style={{ position: 'relative', width: 100, flexShrink: 0 }}>
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          value={open ? query : value}
          onChange={e => { setQuery(e.target.value); setOpen(true) }}
          onFocus={() => { setOpen(true); setQuery('') }}
          onClick={e => { e.target.select(); setOpen(true); setQuery('') }}
          // paddingInlineEnd reserves space for the chevron in both LTR and RTL
          style={{ ...inputStyle, width: '100%', paddingInlineEnd: 28 }}
        />
        {/* insetInlineEnd keeps the chevron at the visual end of the input */}
        <button type="button" onClick={() => setOpen(o => !o)}
          style={{ position: 'absolute', insetInlineEnd: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, color: '#9ca3af', cursor: 'pointer', display: 'flex' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 9 6 6 6-6"/></svg>
        </button>
      </div>
      {open && list.length > 0 && (
        // insetInlineStart aligns the dropdown to the combobox's start edge in RTL
        <div style={{ position: 'absolute', zIndex: 50, top: '100%', insetInlineStart: 0, width: 280, maxHeight: 200, overflowY: 'auto', background: '#fff', border: '1px solid #e5e7eb', borderRadius: radius ?? 6, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', marginTop: 2 }}>
          {list.map(c => (
            <div key={c.id} onMouseDown={() => pick(c.code)} onMouseEnter={() => setHovered(c.id)} onMouseLeave={() => setHovered(null)}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, background: hovered === c.id ? '#f3f4f6' : '#fff', color: '#111827' }}>
              {c.code} {c.country}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
export default function PersonalizedServiceFormRenderer({ cardStyle, lang = 'en', userId }) {
  const searchParams = useSearchParams()

  const [sale,     setSale]     = useState(null)
  const [category, setCategory] = useState(null)
  const [room,     setRoom]     = useState(null)
  const [landSize, setLandSize] = useState('')
  const [landUnit, setLandUnit] = useState(null)
  const [purpose,  setPurpose]  = useState('')
  const [budget,   setBudget]   = useState('')
  const [message,  setMessage]  = useState('')
  const [isLoading,       setIsLoading]       = useState(false)
  const [hasEnquiry,      setHasEnquiry]      = useState(false)
  const [name,            setName]            = useState('')
  const [email,           setEmail]           = useState('')
  const [phone,           setPhone]           = useState('')
  const [line,            setLine]            = useState('')
  const [wechat,          setWechat]          = useState('')
  const [selectedCountry, setSelectedCountry] = useState(countryCodes[0]?.code ?? '+66')
  const [contact,         setContact]         = useState(preferredContact[lang]?.[0])

  useEffect(() => {
    if (!cardStyle?.fields) return
    const sf = cardStyle.fields.find(f => f.id === 'sale')
    const cf = cardStyle.fields.find(f => f.id === 'category')
    const lf = cardStyle.fields.find(f => f.id === 'land_size')
    if (sf?.options?.[0])     setSale(sf.options[0])
    if (cf?.options?.[0])     setCategory(cf.options[0])
    if (lf?.unitOptions?.[0]) setLandUnit(lf.unitOptions[0])
  }, [cardStyle])

  useEffect(() => {
    try {
      const s = JSON.parse(localStorage.getItem('contactDetails'))
      if (!s) return
      if (s.name)            setName(s.name)
      if (s.selectedCountry) setSelectedCountry(s.selectedCountry)
      if (s.phone)           setPhone(s.phone)
      if (s.email)           setEmail(s.email)
      if (s.contact)         setContact(s.contact)
      if (s.line)            setLine(s.line)
      if (s.wechat)          setWechat(s.wechat)
    } catch {}
  }, [])

  const handleForm = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    const cookies = getCookies()
    localStorage.setItem('contactDetails', JSON.stringify({ name, selectedCountry, phone, email, contact, line, wechat }))
    const { sendGAEvent } = await import('@next/third-parties/google')
    sendGAEvent(
      'event',
      'lead_homesearch',
      { 
        // event: 'lead_homesearch', 
        // user_email: email || 'not_provided', 
        // user_phonecall: `${selectedCountry}${phone?.replace(/[a-zA-Z\W]/g, '')}`, 
        listing_category: category, 
        listing_type: sale 
      })
    const fbclid = searchParams.get('fbclid')
    const { sendPersonalForm } = await import('@services/sendPersonalForm')
    await sendPersonalForm({ userId, name, selectedCountry, phone, email, message, sale, category, contact, line, wechat, budget, room, purpose, area: landSize, landUnit, likes: cookies.likes ?? undefined, fbclid: fbclid ?? undefined })
    setHasEnquiry(true)
    setIsLoading(false)
  }

  if (!cardStyle?.fields || !cardStyle?.formStyle) return null
  const { fields, formStyle: fs } = cardStyle
  const categoryVal = category?.value ?? ''
  const contactVal  = contact?.value ?? contact ?? ''

  // ── inputBase uses paddingInlineStart/End so icon padding flips in RTL ───────
  const inputBase = {
    width: '100%',
    paddingTop: '8px', paddingBottom: '8px',
    // icon sits at inline-start; reserve 36px there, 10px at the end
    paddingInlineStart: 36, paddingInlineEnd: 10,
    boxSizing: 'border-box', fontFamily: 'inherit',
    fontSize: `${fs.inputFontSize ?? 0.875}rem`, color: fs.inputColor ?? '#111827',
    background: fs.inputBg ?? '#ffffff', outline: 'none',
    border: `${fs.inputBorderWidth ?? 1}px solid ${fs.inputBorderColor ?? '#d1d5db'}`,
    borderRadius: fs.inputRadius ?? 6,
  }
  // No leading icon — equal padding both sides
  const inputNoIcon = { ...inputBase, paddingInlineStart: 10, paddingInlineEnd: 10 }
  // Select needs extra end-padding for the chevron
  const inputSelect = { ...inputNoIcon, paddingInlineEnd: 32 }

  const lbl = (f) => ({
    display: 'block',
    fontSize:    `${f?.labelSize   ?? fs.labelSize   ?? 0.8125}rem`,
    fontWeight:   f?.labelWeight   ?? fs.labelWeight  ?? '500',
    color:        f?.labelColor    ?? fs.labelColor   ?? '#374151',
    marginBottom: 6,
  })

  const pill = (on) => ({
    flex: 1, padding: '7px 6px', textAlign: 'center', cursor: 'pointer', userSelect: 'none',
    fontSize: `${fs.radioFontSize ?? 0.8125}rem`, fontWeight: fs.radioFontWeight ?? '500',
    borderRadius: fs.radioRadius ?? 6,
    border: `1px solid ${on ? (fs.radioActiveBorderColor ?? '#111827') : (fs.radioBorderColor ?? '#e5e7eb')}`,
    background: on ? (fs.radioActiveBg ?? '#111827') : (fs.radioBg ?? '#ffffff'),
    color:      on ? (fs.radioActiveColor ?? '#ffffff') : (fs.radioColor ?? '#6b7280'),
    transition: 'all 0.15s',
  })

  const box = {
    background:   fs.bg           ?? '#ffffff',
    border:       (fs.borderWidth ?? 1) > 0 ? `${fs.borderWidth ?? 1}px solid ${fs.borderColor ?? '#e5e7eb'}` : 'none',
    borderRadius: fs.borderRadius ?? 8,
    boxShadow:    fs.shadow       ?? 'none',
    padding:      fs.padding      ?? 24,
  }

  // Shared icon wrapper — insetInlineStart so icon sits at visual start in RTL
  const iconWrapper = {
    position: 'absolute', insetInlineStart: 10,
    top: '50%', transform: 'translateY(-50%)',
    color: '#9ca3af', display: 'flex', pointerEvents: 'none',
  }

  // Shared chevron for selects — insetInlineEnd so it stays at the visual end
  const chevron = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"
      style={{ position: 'absolute', insetInlineEnd: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#9ca3af' }}>
      <path d="m6 9 6 6 6-6"/>
    </svg>
  )

  // Required asterisk — marginInlineStart flips with dir="rtl"
  const reqMark = <span style={{ color: '#ef4444', marginInlineStart: 2 }}>*</span>

  const visible = (field) => {
    if (!field.enabled) return false
    if (!field.isConditional) return true
    const { fieldId, values } = field.showWhen
    if (fieldId === 'category') return values.includes(categoryVal)
    if (fieldId === 'contact')  return values.includes(contactVal)
    return true
  }

  // ── Success state ─────────────────────────────────────────────────────────
  if (hasEnquiry) {
    const align = fs.successTitleAlign ?? 'center'
    const flexAlign = align === 'center' ? 'center' : align === 'end' || align === 'right' ? 'flex-end' : 'flex-start'
    return (
      <div style={{ ...box, display: 'flex', flexDirection: 'column', alignItems: flexAlign, gap: 12, textAlign: align }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
        </div>
        <div style={{ fontSize: `${fs.successTitleSize ?? 1.25}rem`, fontWeight: fs.successTitleWeight ?? '700', color: fs.successTitleColor ?? '#111827' }}>
          {r(fs.successTitle, lang) || 'Request Sent!'}
        </div>
        <div style={{ fontSize: `${fs.successSubtitleSize ?? 0.9375}rem`, fontWeight: fs.successSubtitleWeight ?? '400', color: fs.successSubtitleColor ?? '#6b7280', lineHeight: 1.6, maxWidth: 360 }}>
          {r(fs.successSubtitle, lang)}
        </div>
      </div>
    )
  }

  // ── Form ──────────────────────────────────────────────────────────────────
  return (
    <div style={box}>
      {fs.titleEnabled !== false && (
        <div style={{ fontSize: `${fs.titleSize ?? 1.25}rem`, fontWeight: fs.titleWeight ?? '700', color: fs.titleColor ?? '#111827', textAlign: fs.titleAlign ?? 'center', marginBottom: fs.gap ?? 16 }}>
          {r(fs.titleText, lang) || 'Find Your Perfect Property'}
        </div>
      )}

      <form onSubmit={handleForm} style={{ display: 'flex', flexDirection: 'column', gap: fs.gap ?? 16 }}>
        {fields.filter(visible).map(field => {
          const icon    = Icons[field.icon ?? 'none']
          const hasIcon = !!icon
          const label   = r(field.label, lang)
          const ph      = r(field.placeholder, lang)
          const req     = field.required !== false
          // Icon element — uses shared iconWrapper (insetInlineStart)
          const iconEl = hasIcon
            ? <span style={iconWrapper}>{icon}</span>
            : null

          // ── Radio group (sale / category / rooms) ────────────────────────
          if (field.type === 'radio') {
            const isSale = field.id === 'sale'
            const isCat  = field.id === 'category'
            const isRoom = field.id === 'rooms_condo' || field.id === 'rooms_house'
            const activeVal = isSale ? (sale?.value ?? '') : isCat ? categoryVal : isRoom ? (room?.value ?? '') : ''
            const setActive = isSale
              ? (opt) => setSale(opt)
              : isCat
              ? (opt) => { setCategory(opt); setRoom(null) }
              : isRoom
              ? (opt) => setRoom(opt)
              : () => {}
            return (
              <div key={field.id}>
                <label style={lbl(field)}>{label}{req && reqMark}</label>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {(field.options ?? []).map(opt => (
                    <div key={opt.value} onClick={() => setActive(opt)} style={pill(opt.value === activeVal)}>
                      {r(opt.label, lang)}
                    </div>
                  ))}
                </div>
                {req && <input type="text" required value={activeVal} onChange={() => {}} style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }} />}
              </div>
            )
          }

          // ── Land size + unit pills ───────────────────────────────────────
          if (field.type === 'land') return (
            <div key={field.id}>
              <label style={lbl(field)}>{label}{req && reqMark}</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  {iconEl}
                  <input type="number" min="0" step="any" value={landSize} onChange={e => setLandSize(e.target.value)}
                    placeholder={ph} required={req} style={hasIcon ? inputBase : inputNoIcon} />
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  {(field.unitOptions ?? []).map(u => (
                    <div key={u.value} onClick={() => setLandUnit(u)}
                      style={{ ...pill(u.value === (landUnit?.value ?? '')), flex: 'none', padding: '7px 10px', whiteSpace: 'nowrap' }}>
                      {r(u.label, lang)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )

          // ── Contact method select ────────────────────────────────────────
          if (field.id === 'contact') return (
            <div key={field.id}>
              <label style={lbl(field)}>{label}{req && reqMark}</label>
              <div style={{ position: 'relative' }}>
                <select value={contactVal} required={req}
                  onChange={e => setContact(preferredContact[lang]?.find(x => x.value === e.target.value) ?? e.target.value)}
                  style={{ ...inputSelect, appearance: 'none', cursor: 'pointer' }}>
                  {preferredContact[lang]?.map((c, i) => <option key={i} value={c.value}>{c.name}</option>)}
                </select>
                {chevron}
              </div>
            </div>
          )

          // ── Phone + country code ─────────────────────────────────────────
          if (field.id === 'phone') return (
            <div key={field.id}>
              <label style={lbl(field)}>{label}{req && reqMark}</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <CountryCombobox value={selectedCountry} onChange={setSelectedCountry} inputStyle={inputNoIcon} radius={fs.inputRadius ?? 6} />
                <div style={{ position: 'relative', flex: 1 }}>
                  {iconEl}
                  <input type="text" value={phone} onChange={e => setPhone(e.target.value)}
                    placeholder={ph} required={req} style={hasIcon ? inputBase : inputNoIcon} />
                </div>
              </div>
            </div>
          )

          // ── Textarea ─────────────────────────────────────────────────────
          if (field.type === 'textarea') return (
            <div key={field.id}>
              <label style={lbl(field)}>{label}{req && reqMark}</label>
              <textarea value={message} onChange={e => setMessage(e.target.value)}
                placeholder={ph} required={req} rows={field.rows ?? 3}
                style={{ ...inputNoIcon, resize: 'vertical' }} />
            </div>
          )

          // ── Email ────────────────────────────────────────────────────────
          if (field.type === 'email') return (
            <div key={field.id}>
              <label style={lbl(field)}>{label}{req && reqMark}</label>
              <div style={{ position: 'relative' }}>
                {iconEl}
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder={ph} required={req} style={hasIcon ? inputBase : inputNoIcon} />
              </div>
            </div>
          )

          // ── Budget (number) ──────────────────────────────────────────────
          if (field.type === 'number') return (
            <div key={field.id}>
              <label style={lbl(field)}>{label}{req && reqMark}</label>
              <div style={{ position: 'relative' }}>
                {iconEl}
                <input type="number" min="0" value={budget} onChange={e => setBudget(e.target.value)}
                  placeholder={ph} required={req} style={hasIcon ? inputBase : inputNoIcon} />
              </div>
            </div>
          )

          // ── Generic text: name / line / wechat / purpose ─────────────────
          const textVal    = field.id === 'name' ? name : field.id === 'line' ? line : field.id === 'wechat' ? wechat : purpose
          const textSetter = field.id === 'name' ? setName : field.id === 'line' ? setLine : field.id === 'wechat' ? setWechat : setPurpose
          return (
            <div key={field.id}>
              <label style={lbl(field)}>{label}{req && reqMark}</label>
              <div style={{ position: 'relative' }}>
                {iconEl}
                <input type="text" value={textVal} onChange={e => textSetter(e.target.value)}
                  placeholder={ph} required={req} style={hasIcon ? inputBase : inputNoIcon} />
              </div>
            </div>
          )
        })}

        <button type="submit" disabled={isLoading}
          style={{ width: fs.btnFullWidth !== false ? '100%' : 'auto', padding: '10px 20px', marginTop: 4, fontFamily: 'inherit', cursor: isLoading ? 'not-allowed' : 'pointer', fontSize: `${fs.btnFontSize ?? 0.9375}rem`, fontWeight: fs.btnFontWeight ?? '600', color: fs.btnColor ?? '#ffffff', background: isLoading ? `${fs.btnBg ?? '#2563eb'}99` : (fs.btnBg ?? '#2563eb'), border: 'none', borderRadius: fs.btnRadius ?? 6, transition: 'opacity 0.2s' }}>
          {isLoading ? '…' : (r(fs.btnText, lang) || 'Submit Request')}
        </button>
      </form>
    </div>
  )
}